// Uyarlanmış kopya — emsal: beltasapp/frontend/src/shared/ui/izgara/izgara-tuslar.ts + izgara-editor-keys.ts
// Fark: Ctrl+Ok/PageUp/Ctrl+Space/satır taşıma yok; Ctrl+C/X/V burada değil (gerçek pano olaylarıyla,
// grid-core.tsx); F8 eklendi; Enter hücreyi düzenler (yazılamayan hücrede aşağı gider).
import type { EditorNav } from './types'

/** The part of a keyboard event the maps need (keeps them testable without a DOM). */
export interface KeyLike {
  key: string
  shiftKey: boolean
  ctrlKey: boolean
  metaKey: boolean
  altKey: boolean
  preventDefault: () => void
}

export interface GridKeyActions {
  /** Arrow keys; `extend` = Shift held. */
  move: (dRow: number, dCol: number, extend: boolean) => void
  /** Home / End / Ctrl+Home / Ctrl+End. */
  homeEnd: (end: boolean, ctrl: boolean, extend: boolean) => void
  /** Tab / Shift+Tab. Returns false when focus should leave the grid (not swallowed). */
  tab: (back: boolean) => boolean
  /** Enter (back = Shift+Enter). */
  enter: (back: boolean) => void
  /** F2 / printable character (type-to-replace when `seed` is given). */
  edit: (seed?: string) => void
  /** F4 / Alt+Down / Space on a select cell. Returns whether the cell had a list to open. */
  openList: () => boolean
  fillDown: () => void
  clear: () => void
  /** Esc: collapse the selection to the active cell. */
  collapse: () => void
  selectAll: () => void
  undo: () => void
  redo: () => void
  /** F8 / Shift+F8. */
  issue: (back: boolean) => void
}

/** Grid-level key map (no editor open). Returns true when the key was handled. */
export function mapGridKey(e: KeyLike, a: GridKeyActions): boolean {
  const ctrl = e.ctrlKey || e.metaKey
  const k = e.key.length === 1 ? e.key.toLowerCase() : e.key
  const handled = (fn: () => void) => {
    e.preventDefault()
    fn()
    return true
  }

  if (ctrl && !e.altKey) {
    if (k === 'd') return handled(a.fillDown)
    if (k === 'a' && !e.shiftKey) return handled(a.selectAll)
    if (k === 'z' && !e.shiftKey) return handled(a.undo)
    if (k === 'y' || (k === 'z' && e.shiftKey)) return handled(a.redo)
  }

  switch (e.key) {
    case 'ArrowDown':
      if (e.altKey) {
        // Alt+Down opens a select list; elsewhere it is a plain move.
        e.preventDefault()
        if (a.openList()) return true
        a.move(1, 0, e.shiftKey)
        return true
      }
      return handled(() => a.move(1, 0, e.shiftKey))
    case 'ArrowUp':
      return handled(() => a.move(-1, 0, e.shiftKey))
    case 'ArrowRight':
      return handled(() => a.move(0, 1, e.shiftKey))
    case 'ArrowLeft':
      return handled(() => a.move(0, -1, e.shiftKey))
    case 'Home':
    case 'End':
      return handled(() => a.homeEnd(e.key === 'End', ctrl, e.shiftKey))
    case 'Tab': {
      // Decided first: when focus leaves the grid the key must keep its default.
      const stays = a.tab(e.shiftKey)
      if (stays) e.preventDefault()
      return stays
    }
    case 'Enter':
      return handled(() => a.enter(e.shiftKey))
    case 'F2':
      return handled(() => a.edit())
    case 'F4':
      if (a.openList()) {
        e.preventDefault()
        return true
      }
      return false
    case 'F8':
      return handled(() => a.issue(e.shiftKey))
    case 'Escape':
      return handled(a.collapse)
    case 'Delete':
    case 'Backspace':
      return handled(a.clear)
    case ' ':
      if (!ctrl && !e.altKey && a.openList()) {
        e.preventDefault()
        return true
      }
      return false
    default:
      if (e.key.length === 1 && !ctrl && !e.altKey) return handled(() => a.edit(e.key))
      return false
  }
}

/** Editor key map: Enter commit+down, Shift+Enter commit+up, Tab commit+next, Esc cancel. */
export function mapEditorKey(e: KeyLike, done: (commit: boolean, nav: EditorNav) => void): boolean {
  switch (e.key) {
    case 'Escape':
      e.preventDefault()
      done(false, 'none')
      return true
    case 'Enter':
      e.preventDefault()
      done(true, e.shiftKey ? 'up' : 'down')
      return true
    case 'Tab':
      e.preventDefault()
      done(true, e.shiftKey ? 'prev' : 'next')
      return true
    default:
      return false
  }
}
